import React, { useEffect, useRef, useState } from "react";
import { httpGet, httpPost } from "../helpers/httpMethods";
import usePayStack from "../../hooks/usePayStack";
import { loadWallet, naira } from "./walletOn";
import "./wallet.css";

// Wallet panel (2026-10-31): slides in from the right, bottom sheet on phones. Prefix wl-.
// Balance, add money (fund_wallet -> Paystack -> verify_fund_wallet, which credits what Paystack collected, once),
// and recent activity from GET /wallet/me. Shown only when the wallet switch is on for this reader.
const CHIPS = [500, 1000, 2000, 5000];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const when = (d) => {
  const t = new Date(d);
  const days = Math.floor((Date.now() - t.getTime()) / 864e5);
  if (days < 1 && new Date().getDate() === t.getDate()) return `Today, ${t.toLocaleTimeString("en-NG", { hour: "numeric", minute: "2-digit" })}`;
  if (days < 2) return "Yesterday";
  return t.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: t.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
};
const Svg = ({ d, size = 18, w = 1.9 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>
);
const P = {
  x: "M6 6l12 12M18 6L6 18",
  lock: "M6 11h12v10H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3",
  in: "M12 19V5M5 12l7-7 7 7",
  out: "M12 5v14M5 12l7 7 7-7",
  ok: "M5 12l5 5 9-10",
  paper: "M5 4h11l3 3v13H5zM8 9h8M8 13h8M8 17h5",
};

export default function WalletSheet({ open, onClose, email, onChanged }) {
  const { setPayStackConfig, openPaystackNow } = usePayStack();
  const [w, setW] = useState(null);
  const [amount, setAmount] = useState("1000");
  const [stage, setStage] = useState("idle"); // idle | opening | confirming | done | waiting
  const [err, setErr] = useState("");
  const [added, setAdded] = useState(null);
  const ref = useRef("");
  const panel = useRef(null);
  const stageRef = useRef(stage);
  stageRef.current = stage;

  const picked = useRef(false);
  const reload = async () => {
    const v = await loadWallet(true);
    setW(v);
    if (!picked.current && v && v.suggest && v.suggest.amount) { setAmount(String(v.suggest.amount)); picked.current = true; }
  };
  useEffect(() => {
    if (!open) return undefined;
    picked.current = false;
    reload();
    setStage("idle"); setErr(""); setAdded(null);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    const key = (e) => { if (e.key === "Escape" && !["opening", "confirming"].includes(stageRef.current)) onClose(); };
    window.addEventListener("keydown", key);
    setTimeout(() => panel.current && panel.current.focus(), 50);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", key); };
  }, [open]);
  if (!open) return null;

  const limits = (w && w.limits) || { min: 100, max: 500000 };
  const value = Number(String(amount).replace(/[^0-9]/g, "")) || 0;
  const invalid = value < limits.min ? `The smallest top-up is ${naira(limits.min)}` : value > limits.max ? `The largest top-up is ${naira(limits.max)}` : "";
  const busy = stage === "opening" || stage === "confirming";
  const tiers = (w && w.bonus) || [];
  const bonusOf = (n) => { const t = tiers.filter((x) => n >= x.from).pop(); return t ? t.bonus : 0; };
  const nextTier = tiers.find((t) => value < t.from);
  // Four buttons: always the suggested amount and the first bonus level, then the usual amounts.
  const chips = Array.from(new Set([...(w && w.suggest ? [w.suggest.amount] : []), ...(tiers[0] ? [tiers[0].from] : []), ...CHIPS])).slice(0, 4).sort((a, b) => a - b);

  const confirm = async (reference) => {
    setStage("confirming"); setErr("");
    for (let i = 0; i < 8; i += 1) {
      const r = await httpGet(`verify_fund_wallet/${encodeURIComponent(reference)}`);
      if (r && !r.er && r.data) {
        setAdded({ amount: r.data.amount, bonus: r.data.bonus || 0, balance: r.data.balance });
        setStage("done");
        window.dispatchEvent(new Event("tns:wallet-changed"));
        if (onChanged) onChanged();
        reload();
        return;
      }
      const msg = (r && r.er && r.er.message) || "";
      if (/failed/i.test(msg)) { setStage("idle"); setErr("Paystack says this payment failed. Nothing was added, and you weren’t charged."); return; }
      await sleep(2500);
    }
    setStage("waiting");
  };

  const add = async () => {
    if (invalid || busy) return;
    setErr(""); setStage("opening");
    const r = await httpPost("fund_wallet", { amount: value, currency: "NGN" });
    if (!r || r.er || !r.data || !r.data.tx_ref) {
      setStage("idle");
      setErr((r && r.er && r.er.message) || "We couldn’t start the payment. Please try again.");
      return;
    }
    ref.current = r.data.tx_ref;
    setPayStackConfig({ email: r.data.email || email, reference: r.data.tx_ref, amount: value });
    const opened = openPaystackNow(
      () => confirm(ref.current),
      () => { if (stageRef.current === "opening") setStage("idle"); },
      { quiet: true }
    );
    if (!opened) { setStage("idle"); setErr("The payment window didn’t open. Please check your connection and try again."); }
  };

  const balance = w ? w.balance : null;
  const activity = (w && w.activity) || [];

  return (
    <div className="wl-wrap" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <aside className="wl-panel" role="dialog" aria-modal="true" aria-labelledby="wl-title" tabIndex={-1} ref={panel}>
        <span className="wl-grab" aria-hidden="true" />
        <header className="wl-head">
          <h2 id="wl-title">Wallet</h2>
          <button type="button" className="wl-x" onClick={onClose} disabled={busy} aria-label="Close wallet"><Svg d={P.x} /></button>
        </header>

        <div className="wl-body">
          <section className="wl-card" aria-label="Your balance">
            <span className="wl-card-label">Balance</span>
            <strong className="wl-balance" aria-live="polite">{balance === null ? <span className="wl-sk" /> : naira(balance)}</strong>
            {w && w.low ? (
              <span className="wl-card-note wl-low">Not enough for today’s {w.low.paper} ({naira(w.low.need)}). Add money to keep paying in one tap.</span>
            ) : (
              <span className="wl-card-note">Top up once, then pay for papers without typing your card details each time.</span>
            )}
          </section>

          {stage === "done" && added ? (
            <section className="wl-result" role="status">
              <span className="wl-ok"><Svg d={P.ok} size={26} w={2.5} /></span>
              <h3>{naira(added.amount)} added</h3>
              {added.bonus ? <p className="wl-bonus-got">+ {naira(added.bonus)} bonus from us</p> : null}
              <p>Your new balance is <b>{naira(added.balance)}</b>.</p>
              <div className="wl-actions">
                <button type="button" className="wl-btn" onClick={() => { onClose(); if (window.location.pathname !== "/") window.location.href = "/"; }}>Read today’s papers</button>
                <button type="button" className="wl-btn2" onClick={() => { setStage("idle"); setAdded(null); }}>Add more</button>
              </div>
            </section>
          ) : stage === "waiting" ? (
            <section className="wl-result" role="status">
              <span className="wl-wait"><Svg d={P.lock} size={24} /></span>
              <h3>Almost there</h3>
              <p>Paystack is still confirming your payment. Bank transfers can take a few minutes. We’ll add it to your wallet automatically as soon as it arrives, and you won’t be charged twice.</p>
              <div className="wl-actions">
                <button type="button" className="wl-btn" onClick={() => confirm(ref.current)}>Check again</button>
                <button type="button" className="wl-btn2" onClick={onClose}>Close</button>
              </div>
            </section>
          ) : (
            <section className="wl-add" aria-labelledby="wl-add-title">
              <h3 id="wl-add-title">Add money</h3>
              <div className="wl-chips" role="radiogroup" aria-label="Amount">
                {chips.map((c) => (
                  <button type="button" key={c} role="radio" aria-checked={value === c} className={value === c ? "on" : ""} onClick={() => setAmount(String(c))} disabled={busy}>
                    <span>{naira(c)}</span>
                    {bonusOf(c) ? <em>+{naira(bonusOf(c))}</em> : w && w.suggest && w.suggest.amount === c ? <em className="tip">Suggested</em> : null}
                  </button>
                ))}
              </div>
              {w && w.suggest && value === w.suggest.amount ? <p className="wl-suggest">{naira(w.suggest.amount)} {w.suggest.text}.</p> : null}
              <label className="wl-field">
                <span>Or enter an amount</span>
                <span className="wl-input">
                  <b aria-hidden="true">₦</b>
                  <input inputMode="numeric" autoComplete="off" value={value ? value.toLocaleString("en-NG") : ""} placeholder="0"
                    onChange={(e) => setAmount(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} disabled={busy} aria-describedby="wl-hint" />
                </span>
                <small id="wl-hint" className={invalid && value ? "bad" : bonusOf(value) ? "good" : ""}>
                  {invalid && value ? invalid
                    : bonusOf(value) ? `You’ll get ${naira(value + bonusOf(value))} in your wallet (${naira(bonusOf(value))} bonus)`
                    : nextTier ? `Add ${naira(nextTier.from)} or more and get ${naira(nextTier.bonus)} extra`
                    : `From ${naira(limits.min)} to ${naira(limits.max)}`}
                </small>
              </label>
              {err ? <p className="wl-err" role="alert">{err}</p> : null}
              <button type="button" className="wl-btn wl-pay" onClick={add} disabled={!!invalid || busy}>
                {stage === "opening" ? "Opening Paystack…" : stage === "confirming" ? <><span className="wl-spin" aria-hidden="true" />Confirming your payment…</> : `Add ${value ? naira(value) : "money"}`}
              </button>
              <p className="wl-trust"><Svg d={P.lock} size={14} />Secured by Paystack · card, bank transfer or USSD</p>
              <p className="wl-consent">Wallet money can only be spent on TheNewspaperStand and can’t be refunded or withdrawn.</p>
            </section>
          )}

          <section className="wl-activity" aria-labelledby="wl-act-title">
            <h3 id="wl-act-title">Recent activity</h3>
            {!w ? (
              <div className="wl-sk-list"><span className="wl-sk" /><span className="wl-sk" /></div>
            ) : !activity.length ? (
              <p className="wl-empty">Nothing yet. Money you add and papers you pay for with your wallet will show here.</p>
            ) : (
              <ul className="wl-list">
                {activity.map((a) => (
                  <li key={a.id} className={a.status !== "done" ? "dim" : ""}>
                    <span className={`wl-ico ${a.kind}`}><Svg d={a.kind === "in" ? P.in : P.paper} size={16} /></span>
                    <span className="wl-what"><strong>{a.label}</strong><small>{when(a.at)}{a.status === "pending" ? " · Waiting for Paystack" : a.status === "failed" ? " · Not completed" : ""}</small></span>
                    <b className={`wl-amt ${a.kind}`}>{a.status === "done" ? (a.kind === "in" ? "+" : "−") : ""}{naira(a.amount)}</b>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <p className="wl-gift"><a href="/o">Have a gift card or code?</a></p>
            <details className="wl-rules">
            <summary>How your wallet works</summary>
            <ul>
              <li>Your wallet money can only be used on TheNewspaperStand: newspapers, magazines, subscriptions, old issues and other services on the site.</li>
              <li>It can’t be withdrawn to a bank account, sent to another person or refunded, so only add what you plan to use.</li>
              {tiers.length ? <li>Top-up bonuses are wallet money too and follow the same rules.</li> : null}
              <li>Paid but your balance didn’t change? Message us from Help with your payment reference and we’ll put it right.</li>
            </ul>
          </details>
        </div>
      </aside>
    </div>
  );
}
